import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  BuildingOfficeIcon,
  UserIcon,
  BanknotesIcon,
  CalendarIcon,
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  AdjustmentsHorizontalIcon,
  XMarkIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import PartnerDetailsModal from '../components/PartnerDetailsModal';
import PartnerForm from '../components/PartnerForm';
import SponsorshipTiers from './SponsorshipTiers';
import ProspectsPage from './prospects/ProspectsPage';
import { getPartnersByTournament } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import type { Partner } from '../types/content';
import CountUp from 'react-countup';

interface FormattedPartner {
  id: number;
  company: string;
  logo: string;
  contactPerson: string;
  email: string;
  phone: string;
  website?: string;
  tier: string;
  status: string;
  contribution: string;
  contributionAmount?: number;
  startDate: string;
  endDate: string;
  benefits: string[];
  description?: string;
  stats?: string[];
  image?: string;
  tournamentId?: string;
}

const tiers = [
  "All",
  "Title Sponsor",
  "Diamond",
  "Platinum",
  "Gold",
  "Silver",
  "Bronze",
  "Official Airline",
  "Official Bank",
  "Official Beverage",
  "Official Telecom Partner",
  "Official Automotive Brand",
  "Official Insurance Partner",
  "Official Real Estate Partner",
  "Official Media Partner"
];
const statuses = ["All", "Active", "Pending", "Expired"];

interface PartnersProps {
  tournamentId: string;
}

const statusConfig: Record<string, { bg: string; text: string; dot: string }> = {
  Active:  { bg: 'bg-emerald-50',  text: 'text-emerald-700', dot: 'bg-emerald-500' },
  Pending: { bg: 'bg-amber-50',    text: 'text-amber-700',   dot: 'bg-amber-400' },
  Expired: { bg: 'bg-red-50',      text: 'text-red-600',     dot: 'bg-red-400' },
};

function Partners({ tournamentId }: PartnersProps) {
  const [activeSubTab, setActiveSubTab] = useState<'partners' | 'prospects' | 'tiers'>('partners');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTier, setSelectedTier] = useState('All');
  const [selectedStatus, setSelectedStatus] = useState('All');
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [recordsPerPage] = useState(12);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState<FormattedPartner | null>(null);
  const [partners, setPartners] = useState<FormattedPartner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isExporting, setIsExporting] = useState(false);
  const [stats, setStats] = useState({
    totalPartners: 0,
    totalValue: 0,
    activePartners: 0,
    pendingRenewals: 0
  });

  useEffect(() => {
    if (tournamentId) fetchPartners();
  }, [tournamentId]);

  const fetchPartners = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await getPartnersByTournament(tournamentId);
      if (fetchError) throw fetchError;
      if (data) {
        const formattedPartners: FormattedPartner[] = data.map(partner => ({
          id: parseInt(partner.id.replace(/-/g, '').substring(0, 8), 16),
          company: partner.company_name,
          logo: partner.logo_url || `https://placehold.co/300x150/png?text=${encodeURIComponent(partner.company_name)}`,
          contactPerson: partner.contact_person,
          email: partner.email,
          phone: partner.phone,
          website: partner.website,
          tier: partner.tier,
          status: partner.status.charAt(0).toUpperCase() + partner.status.slice(1),
          contribution: `₦${(partner.contribution / 1000000)}M`,
          contributionAmount: partner.contribution,
          startDate: partner.start_date,
          endDate: partner.end_date,
          benefits: partner.benefits || [],
          description: partner.description,
          stats: partner.stats,
          image: partner.image_url,
          tournamentId: partner.tournament_id
        }));
        setPartners(formattedPartners);
        const totalValue = data.reduce((sum, p) => sum + (p.contribution || 0), 0);
        setStats({
          totalPartners: data.length,
          totalValue,
          activePartners: data.filter(p => p.status === 'active').length,
          pendingRenewals: data.filter(p => p.status === 'pending').length,
        });
      }
    } catch (err) {
      setError('Failed to load partners');
      toast.error('Failed to load partners');
    } finally {
      setLoading(false);
    }
  };

  const filteredPartners = useMemo(() => partners.filter(p => {
    const searchMatch = !searchTerm ||
      p.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.contactPerson.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.email.toLowerCase().includes(searchTerm.toLowerCase());
    return searchMatch &&
      (selectedTier === 'All' || p.tier === selectedTier) &&
      (selectedStatus === 'All' || p.status === selectedStatus);
  }), [searchTerm, selectedTier, selectedStatus, partners]);

  const totalPages = Math.ceil(filteredPartners.length / recordsPerPage);
  const currentRecords = filteredPartners.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);

  const exportPartners = () => {
    try {
      setIsExporting(true);
      const headers = ['Company', 'Contact Person', 'Email', 'Phone', 'Website', 'Tier', 'Status', 'Contribution', 'Start Date', 'End Date', 'Benefits'];
      const csvContent = [
        headers.join(','),
        ...filteredPartners.map(p => [
          `"${p.company}"`, `"${p.contactPerson}"`, `"${p.email}"`, `"${p.phone}"`,
          `"${p.website || ''}"`, `"${p.tier}"`, `"${p.status}"`, `"${p.contribution}"`,
          `"${p.startDate}"`, `"${p.endDate}"`, `"${p.benefits.join('; ')}"`
        ].join(','))
      ].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', 'partners.csv');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Partners exported successfully');
    } catch {
      toast.error('Failed to export partners');
    } finally {
      setIsExporting(false);
    }
  };

  const activeFiltersCount = (selectedTier !== 'All' ? 1 : 0) + (selectedStatus !== 'All' ? 1 : 0);

  return (
    <div className="space-y-5">
      {/* Tab Bar */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {(['partners', 'prospects', 'tiers'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveSubTab(tab)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
              activeSubTab === tab
                ? 'bg-white text-golf-gray shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab === 'partners' ? 'Partners' : tab === 'prospects' ? 'Prospects' : 'Sponsorship Tiers'}
          </button>
        ))}
      </div>

      {activeSubTab === 'tiers' ? (
        <SponsorshipTiers />
      ) : activeSubTab === 'prospects' ? (
        <ProspectsPage tournamentId={tournamentId} />
      ) : (
        <div className="space-y-5">
          {/* Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Total Partners', value: stats.totalPartners, isCount: true, icon: BuildingOfficeIcon, color: 'text-blue-500', bg: 'bg-blue-50' },
              { label: 'Total Value', value: stats.totalValue, isCount: false, icon: BanknotesIcon, color: 'text-emerald-500', bg: 'bg-emerald-50' },
              { label: 'Active Partners', value: stats.activePartners, isCount: true, icon: UserIcon, color: 'text-golf-orange', bg: 'bg-orange-50' },
              { label: 'Pending Renewals', value: stats.pendingRenewals, isCount: true, icon: CalendarIcon, color: 'text-amber-500', bg: 'bg-amber-50' },
            ].map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: i * 0.06 }}
                className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-4"
              >
                <div className={`${stat.bg} p-2.5 rounded-xl flex-shrink-0`}>
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                </div>
                <div>
                  <p className="text-xs text-gray-400 font-medium">{stat.label}</p>
                  {stat.isCount ? (
                    <CountUp end={stat.value} duration={1.5} className="text-xl font-bold text-golf-gray" />
                  ) : (
                    <p className="text-xl font-bold text-golf-gray">{formatCurrency(stat.value)}</p>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Toolbar */}
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <MagnifyingGlassIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search by company, contact, or email…"
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-golf-orange/30 focus:border-golf-orange bg-white"
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>

            <button
              onClick={() => setShowFilters(f => !f)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition-all duration-200 ${
                showFilters || activeFiltersCount > 0
                  ? 'bg-golf-orange/10 border-golf-orange/30 text-golf-orange'
                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
              }`}
            >
              <AdjustmentsHorizontalIcon className="w-4 h-4" />
              Filters
              {activeFiltersCount > 0 && (
                <span className="w-4 h-4 rounded-full bg-golf-orange text-white text-[10px] font-bold flex items-center justify-center">
                  {activeFiltersCount}
                </span>
              )}
            </button>

            <button
              onClick={exportPartners}
              disabled={isExporting}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border border-gray-200 bg-white text-gray-600 hover:border-gray-300 transition-colors disabled:opacity-50"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
              Export
            </button>

            <button
              onClick={() => setIsFormOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl bg-golf-orange text-white hover:bg-golf-orange/90 transition-colors"
            >
              <PlusIcon className="w-4 h-4" />
              Add Partner
            </button>
          </div>

          {/* Filter Panel */}
          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="bg-white border border-gray-100 rounded-xl p-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Tier</label>
                    <div className="flex flex-wrap gap-1.5">
                      {['All', 'Title Sponsor', 'Diamond', 'Platinum', 'Gold', 'Silver', 'Bronze'].map(t => (
                        <button
                          key={t}
                          onClick={() => { setSelectedTier(t); setCurrentPage(1); }}
                          className={`px-3 py-1 text-xs font-medium rounded-lg border transition-all ${
                            selectedTier === t
                              ? 'bg-golf-orange text-white border-golf-orange'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Status</label>
                    <div className="flex flex-wrap gap-1.5">
                      {statuses.map(s => (
                        <button
                          key={s}
                          onClick={() => { setSelectedStatus(s); setCurrentPage(1); }}
                          className={`px-3 py-1 text-xs font-medium rounded-lg border transition-all ${
                            selectedStatus === s
                              ? 'bg-golf-orange text-white border-golf-orange'
                              : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Results summary */}
          <div className="flex items-center justify-between">
            <p className="text-xs text-gray-400">
              {loading ? 'Loading…' : `${filteredPartners.length} partner${filteredPartners.length !== 1 ? 's' : ''}`}
              {(searchTerm || activeFiltersCount > 0) && !loading && ' matching filters'}
            </p>
            {(searchTerm || activeFiltersCount > 0) && (
              <button
                onClick={() => { setSearchTerm(''); setSelectedTier('All'); setSelectedStatus('All'); }}
                className="text-xs text-golf-orange hover:underline"
              >
                Clear all
              </button>
            )}
          </div>

          {/* Partner Cards Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 animate-pulse">
                  <div className="h-16 bg-gray-100 rounded-lg mb-4" />
                  <div className="h-3 bg-gray-100 rounded w-3/4 mb-2" />
                  <div className="h-3 bg-gray-100 rounded w-1/2" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
              <p className="text-red-500 text-sm mb-3">{error}</p>
              <button onClick={fetchPartners} className="text-sm text-golf-orange hover:underline">Try again</button>
            </div>
          ) : currentRecords.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-xl border border-gray-100">
              <BuildingOfficeIcon className="w-10 h-10 text-gray-200 mx-auto mb-3" />
              <p className="text-sm text-gray-400">No partners found</p>
              {(searchTerm || activeFiltersCount > 0) && (
                <button
                  onClick={() => { setSearchTerm(''); setSelectedTier('All'); setSelectedStatus('All'); }}
                  className="mt-2 text-xs text-golf-orange hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {currentRecords.map((partner, i) => {
                const sc = statusConfig[partner.status] ?? { bg: 'bg-gray-50', text: 'text-gray-500', dot: 'bg-gray-400' };
                return (
                  <motion.div
                    key={partner.id}
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: i * 0.04 }}
                    onClick={() => { setSelectedPartner(partner); setIsModalOpen(true); }}
                    className="group bg-white rounded-xl border border-gray-100 hover:border-gray-200 hover:shadow-md transition-all duration-200 cursor-pointer overflow-hidden"
                  >
                    {/* Logo area */}
                    <div className="h-24 bg-gray-50 flex items-center justify-center px-6 border-b border-gray-100">
                      <img
                        src={partner.logo}
                        alt={partner.company}
                        className="max-h-16 max-w-full object-contain"
                      />
                    </div>

                    {/* Content */}
                    <div className="p-4 space-y-3">
                      <div>
                        <h3 className="text-sm font-semibold text-golf-gray leading-snug group-hover:text-golf-orange transition-colors">
                          {partner.company}
                        </h3>
                        <p className="text-[11px] text-gray-400 mt-0.5">{partner.tier}</p>
                      </div>

                      <div className="flex items-center justify-between">
                        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${sc.bg} ${sc.text}`}>
                          <span className={`w-1.5 h-1.5 rounded-full ${sc.dot}`} />
                          {partner.status}
                        </span>
                        <span className="text-[11px] font-semibold text-gray-600">{partner.contribution}</span>
                      </div>

                      <div className="pt-2 border-t border-gray-50 space-y-1">
                        <div className="flex items-center gap-2 text-[11px] text-gray-400">
                          <UserIcon className="w-3 h-3 flex-shrink-0" />
                          <span className="truncate">{partner.contactPerson}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] text-gray-400">
                          <CalendarIcon className="w-3 h-3 flex-shrink-0" />
                          <span>{partner.startDate} – {partner.endDate}</span>
                        </div>
                        {partner.website && partner.website !== '#' && (
                          <div className="flex items-center gap-2 text-[11px] text-golf-orange">
                            <GlobeAltIcon className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{partner.website.replace(/^https?:\/\//, '')}</span>
                          </div>
                        )}
                      </div>

                      {partner.benefits.length > 0 && (
                        <div className="flex flex-wrap gap-1 pt-1">
                          {partner.benefits.slice(0, 2).map((b, idx) => (
                            <span key={idx} className="px-2 py-0.5 text-[10px] bg-gray-50 text-gray-500 rounded-md border border-gray-100">
                              {b}
                            </span>
                          ))}
                          {partner.benefits.length > 2 && (
                            <span className="px-2 py-0.5 text-[10px] bg-gray-50 text-gray-400 rounded-md border border-gray-100">
                              +{partner.benefits.length - 2}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 pt-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="w-4 h-4" />
              </button>

              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(n => n === 1 || n === totalPages || Math.abs(n - currentPage) <= 1)
                .reduce<(number | '...')[]>((acc, n, idx, arr) => {
                  if (idx > 0 && n - (arr[idx - 1] as number) > 1) acc.push('...');
                  acc.push(n);
                  return acc;
                }, [])
                .map((n, i) =>
                  n === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-2 text-gray-300 text-sm">…</span>
                  ) : (
                    <button
                      key={n}
                      onClick={() => setCurrentPage(n as number)}
                      className={`w-8 h-8 rounded-lg text-sm font-medium transition-all ${
                        currentPage === n
                          ? 'bg-golf-orange text-white'
                          : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      {n}
                    </button>
                  )
                )}

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="p-2 rounded-lg text-gray-400 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRightIcon className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>
      )}

      <PartnerDetailsModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        partner={selectedPartner}
        onUpdate={() => fetchPartners()}
        onDelete={() => fetchPartners()}
        onDuplicate={() => fetchPartners()}
      />

      <PartnerForm
        isOpen={isFormOpen}
        isAdmin={true}
        defaultTournamentId={tournamentId}
        onClose={() => { setIsFormOpen(false); fetchPartners(); }}
      />
    </div>
  );
}

export default Partners;
